import { useState, useEffect, useRef } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  const [counters, setCounters] = useState({
    experience: 0,
    clients: 0,
    specialists: 0,
    cities: 0,
  });
  const [aboutVisible, setAboutVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [offerVisible, setOfferVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const aboutRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const offerRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === aboutRef.current) setAboutVisible(true);
            if (entry.target === cardsRef.current) setCardsVisible(true);
            if (entry.target === offerRef.current) setOfferVisible(true);
            if (entry.target === statsRef.current) setStatsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (aboutRef.current) observer.observe(aboutRef.current);
    if (cardsRef.current) observer.observe(cardsRef.current);
    if (offerRef.current) observer.observe(offerRef.current);
    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsVisible) return;
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      experience: 20,
      clients: 10000,
      specialists: 5000,
      cities: 13,
    };

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounters({
        experience: Math.floor(targets.experience * progress),
        clients: Math.floor(targets.clients * progress),
        specialists: Math.floor(targets.specialists * progress),
        cities: Math.floor(targets.cities * progress),
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounters(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [statsVisible]);

  const values = [
    { icon: 'ri-heart-pulse-line', title: 'العناية الشخصية', description: 'نهتم بكل عميل بشكل فردي ونقدم خدمة مخصصة' },
    { icon: 'ri-shield-check-line', title: 'الخصوصية والأمان', description: 'نضمن خصوصية عملائنا التامة في جميع المراحل' },
    { icon: 'ri-award-line', title: 'الجودة والاحترافية', description: 'نلتزم بأعلى معايير الجودة في خدماتنا' },
    { icon: 'ri-hand-heart-line', title: 'التفهّم الكامل', description: 'نتفهم كافة ظروف عملائنا الخاصة ونتعامل معها بأفضل الأساليب' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#184187] to-[#2563a8] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-[fadeInUp_0.8s_ease-out]">من نحن</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed animate-[fadeInUp_1s_ease-out]">
              فريق من الأخصائيين السمعيين السعوديين المؤهلين
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section ref={aboutRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div
              className={`text-center mb-16 transition-all duration-1000 ${
                aboutVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">نبذة عنا</h2>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                نخبة من الأخصائيين والاستشاريين السعوديين المميزين يسعدون بتقديم تجربة فريدة لاختبارات السمع وخدمات السماعات الطبية، مباشرة في منزلك أو مكان عملك. نفهم صعوبة خروج بعض العملاء، خاصة كبار السن أو من لديهم ظروف خاصة، لذلك قررنا أن تتجه العيادات إليك أينما كنت، مع فريق من الأخصائيين السعوديين المؤهلين.
              </p>
            </div>

            <div ref={cardsRef} className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Vision Card */}
              <div
                className={`bg-[#184187] rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 ${
                  cardsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-4xl"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">رؤيتنا</h3>
                <p className="text-lg leading-relaxed text-white/90">
                  نسعى لأن نكون الخيار الأول في المملكة لخدمات السمع المنزلية، من خلال تقديم رعاية صحية متميزة تصل إلى كل منزل، مع الحفاظ على أعلى معايير الجودة والاحترافية في تقديم الخدمات السمعية.
                </p>
              </div>

              {/* Mission Card */}
              <div
                className={`bg-[#0e7490] rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-500 delay-200 hover:-translate-y-1 ${
                  cardsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-compass-line text-4xl"></i>
                </div>
                <h3 className="text-3xl font-bold mb-4">رسالتنا</h3>
                <p className="text-lg leading-relaxed text-white/90">
                  تقديم خدمات سمعية احترافية في منازل عملائنا، من خلال فريق من الأخصائيين السعوديين المؤهلين، مع ضمان الخصوصية والراحة، والمساهمة في تحسين جودة حياة من يعانون من مشاكل السمع.
                </p>
              </div>
            </div>

            {/* Values Section */}
            <div className="mb-16">
              <div className="text-center mb-10">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">قيمنا</h3>
                <p className="text-lg text-gray-600">المبادئ التي نؤمن بها ونعمل وفقها</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {values.map((value, index) => (
                  <div
                    key={index}
                    className={`bg-gray-50 rounded-2xl p-6 text-center hover:bg-[#184187] hover:text-white group transition-all duration-500 hover:-translate-y-2 hover:shadow-xl cursor-default ${
                      cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${300 + index * 100}ms` }}
                  >
                    <div className="w-16 h-16 bg-[#184187]/10 group-hover:bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-500">
                      <i className={`${value.icon} text-3xl text-[#184187] group-hover:text-white transition-colors duration-500`}></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 group-hover:text-white mb-2 transition-colors duration-500">{value.title}</h4>
                    <p className="text-sm text-gray-600 group-hover:text-white/90 leading-relaxed transition-colors duration-500">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Image Section */}
            <div className="mb-16 rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://public.readdy.ai/ai/img_res/e9a7559d77277569c18073f80ff9e50d.jpg"
                alt="عيادة سماعتي"
                className="w-full h-[500px] object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
            </div>

            {/* What We Offer */}
            <div ref={offerRef} className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-16">
              <h3
                className={`text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center transition-all duration-1000 ${
                  offerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                ما نقدمه لك
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: 'ri-stethoscope-line', text: 'اختبارات قياس السمع المبدئية والمتقدمة' },
                  { icon: 'ri-settings-3-line', text: 'برمجة وإعادة برمجة جميع أنواع السماعات الطبية' },
                  { icon: 'ri-tools-line', text: 'استلام السماعات للصيانة وأخذ مقاسات الأذن' },
                  { icon: 'ri-customer-service-2-line', text: 'التواصل مع شركات السماعات نيابة عن العملاء' },
                ].map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 ${
                      offerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${(index + 1) * 150}ms` }}
                  >
                    <div className="w-12 h-12 bg-[#184187] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Our Goal & Motto */}
            <div className="text-center mb-16">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">هدفنا</h3>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                العناية الشخصية، ضمان الخصوصية، وتحسين جودة حياتك، خصوصاً لمن يصعب عليهم زيارة العيادات.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#184187] to-[#2563a8] rounded-2xl p-12 text-center text-white shadow-2xl">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-pulse-line text-5xl"></i>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4">شعارنا</h3>
              <p className="text-2xl md:text-3xl font-semibold leading-relaxed">
                العناية الخاصة بسماعتك وسمعك أصبحت مسؤوليتنا
              </p>
              <p className="text-xl mt-4 text-white/90">حلول مبتكرة لتحسين سمعك</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section ref={statsRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { icon: 'ri-verified-badge-line', value: 'خبرة وكفاءة', label: 'أخصائيون مؤهلون', isText: true },
              { icon: 'ri-user-heart-line', value: `${counters.clients.toLocaleString()}+`, label: 'عميل راضٍ' },
              { icon: 'ri-team-line', value: `${counters.specialists.toLocaleString()}+`, label: 'أخصائي مؤهل' },
              { icon: 'ri-map-pin-line', value: `${counters.cities}+`, label: 'مدينة نخدمها' },
            ].map((stat, index) => (
              <div
                key={index}
                className={`text-center bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 ${
                  statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="w-16 h-16 bg-[#184187] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-white`}></i>
                </div>
                <div className={`font-bold text-[#184187] mb-2 ${'isText' in stat && stat.isText ? 'text-2xl' : 'text-4xl md:text-5xl'}`}>{stat.value}</div>
                <div className="text-base text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
